import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import axios from 'axios';
import { apiWithAuth } from '../../lib/api';
import {
  ChevronLeftIcon,
  AcademicCapIcon,
  PlusIcon,
  TrashIcon,
  CalendarIcon,
  CheckCircleIcon,
  DocumentIcon,
  PhotoIcon,
  XMarkIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

// Use shared absolute API base (works on local and prod)

const Qualifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [qualifications, setQualifications] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    qualification_name: '',
    qualification_image: null,
    qualification_file: null,
    obtained_date: ''
  });

  const hasPending = qualifications.some?.(q => q.status === 'pending');

  useEffect(() => {
    if (!user || user.user_type !== 'job_seeker') {
      navigate('/');
      return;
    }
    fetchQualifications();
  }, [user, navigate]);

  const fetchQualifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);
      const response = await client.get('/qualifications');
      setQualifications(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch qualifications:', error);
      toast('資格情報の取得に失敗しました。サンプルデータを表示します。', { icon: '' });
      // サンプルデータを表示（API未実装の場合）
      setQualifications([
        {
          id: '1',
          qualification_name: '普通自動車運転免許',
          obtained_date: '2020-04-15',
          status: 'approved',
          submitted_at: '2024-01-15'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB制限
        toast.error('ファイルサイズは5MB以下にしてください');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          qualification_image: e.target.result, // preview (data URL)
          qualification_file: file,            // original file for upload
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.qualification_name.trim()) {
      toast.error('資格名を入力してください');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);

      // 可能なら multipart/form-data を使用
      const fd = new FormData();
      fd.append('qualification_name', formData.qualification_name);
      if (formData.obtained_date) fd.append('obtained_date', formData.obtained_date);
      if (formData.qualification_file instanceof File) {
        fd.append('qualification_image', formData.qualification_file);
      } else if (formData.qualification_image) {
        // dataURL を Blob に変換して添付
        const dataUrl = formData.qualification_image;
        const arr = dataUrl.split(',');
        const mimeMatch = arr[0].match(/:(.*?);/);
        const mime = mimeMatch ? mimeMatch[1] : 'image/png';
        const bstr = atob(arr[1] || '');
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        const blob = new Blob([u8arr], { type: mime });
        fd.append('qualification_image', blob, `qualification.${mime.split('/')[1] || 'png'}`);
      }

      let ok = false;
      let created = null;
      try {
        const { data } = await client.post('/qualifications', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        ok = true;
        created = data || null;
      } catch (_) {
        // マルチパートが未対応のバックエンド向けにJSONをフォールバック
        const { data } = await client.post('/qualifications', {
          qualification_name: formData.qualification_name,
          obtained_date: formData.obtained_date,
          qualification_image: formData.qualification_image,
        });
        ok = true;
        created = data || null;
      }

      if (ok) {
        toast.success('資格を提出しました（確認中）');

        // 楽観的にリストへ pending として反映
        const optimistic = {
          id: (created && created.id) || `tmp-${Date.now()}`,
          qualification_name: formData.qualification_name,
          obtained_date: formData.obtained_date,
          qualification_image: formData.qualification_image,
          status: 'pending',
          submitted_at: new Date().toISOString(),
        };
        setQualifications(prev => [optimistic, ...prev]);

        // 管理者通知（存在すれば）。失敗しても無視
        try {
          await client.post('/admin/qualifications/notify', {
            qualification_id: optimistic.id,
            action: 'submitted'
          }).catch(async () => {
            // 代替ルート
            await client.post('/admin/notifications', {
              type: 'qualification_submitted',
              message: `${formData.qualification_name} の資格提出がありました。`
            });
          });
        } catch (_) {}
      }
      setShowAddForm(false);
      setFormData({
        qualification_name: '',
        qualification_image: null,
        qualification_file: null,
        obtained_date: ''
      });
      // 最新状態を再取得（承認/却下が即時に反映される環境用）
      fetchQualifications();
    } catch (error) {
      console.error('Failed to create qualification:', error);
      toast.error('資格の登録に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (qualificationId) => {
    if (!window.confirm('この資格を削除しますか？')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);
      await client.delete(`/qualifications/${qualificationId}`);
      
      toast.success('資格を削除しました');
      fetchQualifications();
    } catch (error) {
      console.error('Failed to delete qualification:', error);
      toast.error('資格の削除に失敗しました');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-4 h-4 mr-1" />
            承認済
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XMarkIcon className="w-4 h-4 mr-1" />
            再提出が必要
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="w-4 h-4 mr-1" />
            確認中
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center">
            <button onClick={() => navigate('/mypage')} className="mr-3">
              <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">保有資格</h1>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6">
        {/* 警告（不正防止） */}
        <div className="mb-5 bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          <h2 className="text-sm font-semibold mb-1">重要なお知らせ（不正提出の禁止）</h2>
          <p className="text-sm leading-6">
            虚偽の資格情報や偽造された証明書画像を提出した場合、<strong>アカウントの永久停止</strong>、
            <strong>以後のご登録・ご利用の禁止</strong>、および必要に応じて<strong>法的措置</strong>を講じます。
            正確で真正な情報のみを提出してください。
          </p>
        </div>
        {/* Add Button */}
        {hasPending && (
          <div className="mb-4 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg p-3 text-sm">
            審査中の資格があります。承認が完了するまで新規の提出はできません。
          </div>
        )}
        <button
          onClick={() => setShowAddForm(true)}
          disabled={hasPending}
          className={`w-full mb-6 py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center ${hasPending ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          資格を追加
        </button>

        {/* Add Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">資格を追加</h2>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* 警告（不正提出の禁止） */}
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3">
                  <p className="text-xs leading-5">
                    偽造・改ざんされた証明書の提出は固く禁止されています。違反が判明した場合、
                    アカウントの<strong>永久停止</strong>や<strong>法的措置</strong>の対象となります。
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    資格名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.qualification_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, qualification_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="例：普通自動車第一種運転免許"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    取得日
                  </label>
                  <input
                    type="date"
                    value={formData.obtained_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, obtained_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    資格証明書（画像）
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                    <div className="space-y-1 text-center">
                      {formData.qualification_image ? (
                        <div className="relative">
                          <img
                            src={formData.qualification_image}
                            alt="Preview"
                            className="mx-auto h-32 w-auto rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, qualification_image: null }))}
                            className="absolute top-0 right-0 -mr-2 -mt-2 bg-red-500 text-white rounded-full p-1"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                          <div className="flex text-sm text-gray-600">
                            <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                              <span>ファイルを選択</span>
                              <input
                                type="file"
                                className="sr-only"
                                accept="image/*"
                                onChange={handleImageUpload}
                              />
                            </label>
                          </div>
                          <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-400 transition-colors"
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {submitting ? '登録中...' : '登録'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Qualifications List */}
        {qualifications.length === 0 ? (
          <div className="bg-white rounded-lg p-8 text-center">
            <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">保有資格がありません</h3>
            <p className="text-gray-500 mb-4">資格を追加して、より多くの求人にアピールしましょう</p>
          </div>
        ) : (
          <div className="space-y-4">
            {qualifications.map((qualification) => (
              <div key={qualification.id} className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <AcademicCapIcon className="h-5 w-5 text-blue-600 mr-2" />
                      <h3 className="font-medium text-gray-900">{qualification.qualification_name}</h3>
                    </div>
                    
                    {qualification.obtained_date && (
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        取得日: {new Date(qualification.obtained_date).toLocaleDateString('ja-JP')}
                      </div>
                    )}
                    
                    <div className="mb-3">
                      {getStatusBadge(qualification.status)}
                    </div>

                    {qualification.status === 'rejected' && qualification.rejection_reason && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                        <p className="text-sm text-red-700">
                          <strong>却下理由:</strong> {qualification.rejection_reason}
                        </p>
                      </div>
                    )}

                    {qualification.qualification_image && (
                      <div className="mt-3">
                        <img
                          src={qualification.qualification_image}
                          alt="資格証明書"
                          className="h-24 w-auto rounded-lg border border-gray-200"
                        />
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handleDelete(qualification.id)}
                    className="ml-4 text-red-500 hover:text-red-700 p-1"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Qualifications;